// Named constants for string-literal values used across the Templates pages.
// Values mirror Django enums in the Frontera backend
// (api-django/frontera/api/enum/report_*.py and api/models/...). When adding
// a new enum value here, update the backend definition too.
import { PayorTypeEnum } from "@api/rest/types-and-hooks";

export const ClipboardItemType = {
  SECTION: "section",
  FIELD: "field",
} as const;
export type ClipboardItemTypeValue = (typeof ClipboardItemType)[keyof typeof ClipboardItemType];

export const StructureSubTab = {
  NARRATIVE: "narrative",
  PROGRAMMING: "programming",
} as const;
export type StructureSubTabValue = (typeof StructureSubTab)[keyof typeof StructureSubTab];

export const ReportType = {
  INITIAL_ASSESSMENT: "INITIAL_ASSESSMENT",
  REASSESSMENT: "REASSESSMENT",
  DIAGNOSIS_EVALUATION: "DIAGNOSIS_EVALUATION",
} as const;
export type ReportTypeValue = (typeof ReportType)[keyof typeof ReportType];

export const REPORT_TYPE_LABELS: Record<ReportTypeValue, string> = {
  [ReportType.INITIAL_ASSESSMENT]: "Initial Assessment",
  [ReportType.REASSESSMENT]: "Reassessment",
  [ReportType.DIAGNOSIS_EVALUATION]: "Diagnosis Evaluation",
};

export const REPORT_TYPE_ABBREVIATIONS: Record<ReportTypeValue, string> = {
  [ReportType.INITIAL_ASSESSMENT]: "IA",
  [ReportType.REASSESSMENT]: "RA",
  [ReportType.DIAGNOSIS_EVALUATION]: "DE",
};

export const FieldType = {
  SIMPLE_TEXT: "SIMPLE_TEXT",
  WYSIWYG: "WYSIWYG",
  SELECT: "SELECT",
  CHECKBOX: "CHECKBOX",
  RADIO: "RADIO",
  DATE: "DATE",
  DATETIME: "DATETIME",
  TABLE: "TABLE",
  IMAGE: "IMAGE",
  ASSESSMENT_IMAGE: "ASSESSMENT_IMAGE",
  ASSESSMENT_APPOINTMENTS: "ASSESSMENT_APPOINTMENTS",
  COORDINATION_OF_CARE: "COORDINATION_OF_CARE",
  DSM_TABLE: "DSM_TABLE",
  TREATMENT_OPTION_TABLE: "TREATMENT_OPTION_TABLE",
  TREATMENT_PLAN: "TREATMENT_PLAN",
  BLANK_SIGNATURE_LINE: "BLANK_SIGNATURE_LINE",
  TYPED_SIGNATURE: "TYPED_SIGNATURE",
  WET_SIGNATURE: "WET_SIGNATURE",
} as const;
export type FieldTypeValue = (typeof FieldType)[keyof typeof FieldType];

export const FIELD_TYPES_WITH_OPTIONS: readonly FieldTypeValue[] = [
  FieldType.SELECT,
  FieldType.CHECKBOX,
  FieldType.RADIO,
];

// Field types whose content is managed as structured JSON (TableBuilderDynamic
// or other custom non-text renderers) and that don't expose a plain text/wysiwyg
// editor in the Structure tab.
export const TABLE_FIELD_TYPES: readonly FieldTypeValue[] = [
  FieldType.TABLE,
  FieldType.ASSESSMENT_APPOINTMENTS,
  FieldType.COORDINATION_OF_CARE,
  FieldType.DSM_TABLE,
  FieldType.TREATMENT_OPTION_TABLE,
  FieldType.IMAGE,
  FieldType.ASSESSMENT_IMAGE,
  FieldType.BLANK_SIGNATURE_LINE,
  FieldType.TYPED_SIGNATURE,
  FieldType.WET_SIGNATURE,
  FieldType.DATE,
  FieldType.DATETIME,
  FieldType.TREATMENT_PLAN,
];

// Capture methods a WET_SIGNATURE field's content JSON envelope can allow
// (`{"allowed_methods": [...]}`) — mirrors the backend's
// `WET_SIGNATURE_METHODS` tuple (api/services/wet_signature_content.py).
export const WetSignatureMethod = {
  TYPED: "typed",
  UPLOADED: "uploaded",
  DRAWN: "drawn",
} as const;
export type WetSignatureMethodValue = (typeof WetSignatureMethod)[keyof typeof WetSignatureMethod];

export const WET_SIGNATURE_METHODS: readonly WetSignatureMethodValue[] = [
  WetSignatureMethod.TYPED,
  WetSignatureMethod.UPLOADED,
  WetSignatureMethod.DRAWN,
];

export const WET_SIGNATURE_METHOD_LABELS: Record<WetSignatureMethodValue, string> = {
  [WetSignatureMethod.TYPED]: "Typed",
  [WetSignatureMethod.UPLOADED]: "Uploaded",
  [WetSignatureMethod.DRAWN]: "Drawn",
};

export const TPFieldType = {
  GOAL: "GOAL",
  TARGET: "TARGET",
} as const;
export type TPFieldTypeValue = (typeof TPFieldType)[keyof typeof TPFieldType];

// Native field identifiers with dedicated Programming-tab handling.
// goal_name (ReportGoal.goal_name) and target_name (ReportTarget.target_name)
// back NOT NULL columns, so those two rows can never be turned off or
// deleted; every other native field — mastery_criteria /
// assessment_tool_source included — is freely disable/deletable per
// template (nullable columns). Native field_identifiers are immutable
// regardless. Mirrors the backend's guards in
// api/services/report_template_service.py.
export const GoalFieldIdentifier = {
  GOAL_NAME: "goal_name",
  PROGRESS_SUMMARY: "progress_summary",
} as const;

export const TargetFieldIdentifier = {
  TARGET_NAME: "target_name",
  PROGRESS_SUMMARY: "progress_summary",
} as const;

export const AIGenerationConfig = {
  GOALS_AND_TARGETS: "GOALS_AND_TARGETS",
  GOALS_ONLY: "GOALS_ONLY",
  NO_AI_GENERATION: "NO_AI_GENERATION",
} as const;
export type AIGenerationConfigValue = (typeof AIGenerationConfig)[keyof typeof AIGenerationConfig];

export const TemplateStatus = {
  DRAFT: "Draft",
  PUBLISHED: "Published",
  ARCHIVED: "Archived",
} as const;
export type TemplateStatusValue = (typeof TemplateStatus)[keyof typeof TemplateStatus];

export const PublishMode = {
  NEW: "new",
  REPLACES: "replaces",
} as const;
export type PublishModeValue = (typeof PublishMode)[keyof typeof PublishMode];

export const ExportFramework = {
  HANDLEBARS: "HANDLEBARS",
  GOOGLE_DOCS: "GOOGLE_DOCS",
  DOCXTEMPLATER: "DOCXTEMPLATER",
} as const;
export type ExportFrameworkValue = (typeof ExportFramework)[keyof typeof ExportFramework];

export const RioTab = {
  CHAT: "chat",
  EDITS: "edits",
} as const;
export type RioTabValue = (typeof RioTab)[keyof typeof RioTab];

export const RioMessageRole = {
  USER: "USER",
  ASSISTANT: "ASSISTANT",
  SYSTEM: "SYSTEM",
} as const;
export type RioMessageRoleValue = (typeof RioMessageRole)[keyof typeof RioMessageRole];

export const RioProgressEventType = {
  TOOL_CALLED: "tool_called",
  TOOL_COMPLETED: "tool_completed",
  MODEL_FINALIZED: "model_finalized",
} as const;
export type RioProgressEventTypeValue = (typeof RioProgressEventType)[keyof typeof RioProgressEventType];

export const RioEditTone = {
  ADD: "add",
  UPDATE: "update",
  REMOVE: "remove",
  MOVE: "move",
  NEUTRAL: "neutral",
} as const;
export type RioEditToneValue = (typeof RioEditTone)[keyof typeof RioEditTone];

export const PAYOR_TYPE_LABELS: Record<PayorTypeEnum, string> = {
  [PayorTypeEnum.BCBS]: "BCBS",
  [PayorTypeEnum.UNITED_HEALTHCARE]: "United Healthcare",
  [PayorTypeEnum.AETNA]: "Aetna",
  [PayorTypeEnum.CIGNA]: "Cigna",
  [PayorTypeEnum.HUMANA]: "Humana",
  [PayorTypeEnum.ANTHEM]: "Anthem",
  [PayorTypeEnum.KAISER_PERMANENTE]: "Kaiser Permanente",
  [PayorTypeEnum.TRICARE]: "Tricare",
  [PayorTypeEnum.MAGELLAN_HEALTH]: "Magellan Health",
  [PayorTypeEnum.BEACON_HEALTH_OPTIONS]: "Beacon Health Options",
  [PayorTypeEnum.MEDICAID]: "Medicaid",
  [PayorTypeEnum.CATALIGHT]: "Catalight",
  [PayorTypeEnum.OPTUM]: "Optum",
  [PayorTypeEnum.BSW]: "BSW",
  [PayorTypeEnum.EVERNORTH]: "Evernorth",
  [PayorTypeEnum.MOLINA]: "Molina",
  [PayorTypeEnum.EMI_HEALTH]: "EMI Health",
};
